import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { container } from '../context/container';
import { LoginService, SessionState } from './login.service';
import { RefreshService } from './refresh.service';
import { ProactiveRefreshScheduler } from './proactive-refresh.scheduler';
import { CrossTabAuthService } from './cross-tab-auth.service';
import { StepUpService } from './step-up.service';
import { AuthService } from './auth.service';
import { IdentityUnavailableError, LightHttp } from './light-http.service';
import { BrowserUtils } from '../ui-utils/browser-utils';
import { isAccessTokenStale, isAnonymousToken } from '../utils/token-expiry.util';
import { LoginResponse } from '../utils/request.utils';
import { Logger } from '../models/logger';

const logger = Logger.getLogger('SessionBootstrap');
let providersWired = false;
let lifecycleWired = false;

/** R2-B2 presence marker professorx sets alongside the httpOnly __Secure-rt. */
function hasPresenceCookie(): boolean {
    return typeof document !== 'undefined' &&
        document.cookie.split('; ').some((c) => c.startsWith('rt_present='));
}

/**
 * Register the LightHttp refresh/step-up providers + enable reactive refresh-if-stale + cross-tab.
 * Idempotent. Providers are registered BEFORE enabling so an in-flight request never sees
 * refreshEnabled with a null provider.
 */
function wireProviders(): void {
    if (providersWired) return;
    if (!BrowserUtils.hasWindow()) return;
    providersWired = true;
    LightHttp.registerRefreshProvider(container.get(RefreshService));
    LightHttp.registerStepUpProvider(container.get(StepUpService));
    LightHttp.setRefreshEnabled(true);
    container.get(CrossTabAuthService).init();
}

/**
 * Drive the proactive refresh scheduler off session state so it starts on EVERY login — bootstrap
 * auto-login AND interactive login — and stops on logout. Set up once (browser-only).
 *
 * Gated on the `rt_present` cookie, NOT the `refreshTokens` settings flag: the backend sets
 * rt_present only when it actually issued a refresh session, so it's the authoritative "refresh
 * mode is active for this browser" signal — and, unlike the flag, it's available synchronously.
 * (The flag's first emission comes from the SSG-baked settings, i.e. `false`, before the live
 * /settings arrives; gating on it skipped the startup refresh and logged idle users out on reload
 * even though their year-long refresh token was valid.) A flag-off session never gets the cookie,
 * so the machinery stays inert — the kill switch still holds.
 */
function wireSchedulerLifecycle(): void {
    if (lifecycleWired) return;
    if (!BrowserUtils.hasWindow()) return;
    lifecycleWired = true;
    const scheduler = container.get(ProactiveRefreshScheduler);
    container.get(LoginService).onSessionState.subscribe((s) => {
        if (s === SessionState.LOGGED_IN && hasPresenceCookie()) {
            wireProviders();
            scheduler.start();
        } else if (s === SessionState.ANONYMOUS) {
            try { scheduler.stop(); } catch { /* best-effort teardown */ }
        }
    });
}

/**
 * Startup bootstrap. REPLACES the bare boot login(null,null,null) call in _app.tsx; returns the
 * same LoginResponse observable.
 *
 * Drives off the `rt_present` cookie (authoritative refresh-session signal), not the settings flag:
 *  - rt_present present + access token missing/anonymous/STALE → refresh() FIRST, then login()
 *    (this is what keeps an idle user logged in on reload — the year-long refresh token is finally
 *    used instead of dropping to anonymous).
 *  - rt_present present + fresh token → login() (machinery starts via the LOGGED_IN lifecycle).
 *  - NO rt_present → a single login(null) (today's behavior). A valid legacy bearer + flag-on gets
 *    upgraded to a session by professorx (sets rt_present); the resulting LOGGED_IN then wires up.
 * Guard: a transient (status 0) is swallowed (stay anon-capable, NEVER clearLocalSession); a
 * definitive 401/403 propagates to the caller's existing self-heal.
 */
export function bootstrapSession(): Observable<LoginResponse> {
    const loginService = container.get(LoginService);
    if (!BrowserUtils.hasWindow()) {
        return loginService.login(null, null, null);
    }
    // Set up unconditionally so a LATER interactive login also starts the scheduler / enables refresh.
    wireSchedulerLifecycle();

    const token = container.get(AuthService).getToken();
    const hasRt = hasPresenceCookie();

    // pre$ (refresh-first) → login(null) → swallow transient (stay anon-capable), propagate 401/403.
    const finalize = (pre$: Observable<unknown>): Observable<LoginResponse> =>
        pre$.pipe(
            mergeMap(() => loginService.login(null, null, null)),
            catchError((err) => {
                const status = (err && (err as any).status) || 0;
                if (status === 401 || status === 403) return throwError(() => err);
                // F3g: no identity after the 429 retries is not transient for this page -- every identity-bound call would
                // fail. It reaches `_app.tsx`, which shows a NON-BLOCKING notice with Retry (F3r: never a dialog -- finished
                // downloads need no identity) instead of a silent anon-capable boot.
                if (err instanceof IdentityUnavailableError) return throwError(() => err);
                logger.info({ message: 'Startup bootstrap exchange failed (transient); staying anon-capable', error: err });
                return of({ error: true, registered: false } as unknown as LoginResponse);
            }),
        );

    if (hasRt) {
        // Enable refresh BEFORE any request, then silently refresh a missing/stale access token so
        // the auto-login below uses a FRESH bearer.
        wireProviders();
        const staleOrMissing = !token || isAnonymousToken(token) || isAccessTokenStale(token);
        const pre$: Observable<unknown> = staleOrMissing
            ? container.get(RefreshService).refresh().pipe(
                catchError((err) => {
                    logger.info({ message: 'Startup refresh failed; continuing boot', error: err });
                    return of(null);
                }),
            )
            : of(null);
        return finalize(pre$);
    }

    return finalize(of(null));
}
